// Minimal, safe markdown → React for assistant answers. Handles what the
// model actually produces (headings, paragraphs, bullet/numbered lists,
// bold, italic, inline code, blockquotes) and turns [n] into citation
// chips. No HTML passthrough, so transcript text can never inject markup.
import React, { useEffect, useRef, useState } from "react";

export type CitationRenderer = (n: number) => React.ReactNode;

const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`]+`|(?:\[\d+\])+)/g;

// Streaming fade: while an answer is arriving, every word and inline token
// is wrapped in a `.chat-fade` span keyed by its position. React keeps the
// already-mounted spans (their animation has finished) and only the newly
// appended ones mount, so each new word fades in once — the ChatGPT effect.
// Keys are positional (block, part, word), which stay stable as text is
// appended; only the tail of the current paragraph can shift when a `**`
// or `[n]` closes and re-splits it, and that re-fade is invisible.
function fade(key: string, node: React.ReactNode, on: boolean): React.ReactNode {
  if (!on) return <React.Fragment key={key}>{node}</React.Fragment>;
  return (
    <span key={key} className="chat-fade">
      {node}
    </span>
  );
}

function words(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(/(\s+)/).map((w, j) =>
    /^\s+$/.test(w) || !w ? (
      <React.Fragment key={`${keyPrefix}${j}`}>{w}</React.Fragment>
    ) : (
      <span key={`${keyPrefix}${j}`} className="chat-fade">
        {w}
      </span>
    )
  );
}

export function renderInline(
  text: string,
  cite: CitationRenderer,
  keyPrefix = "",
  animate = false
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const parts = text.split(INLINE_RE);
  parts.forEach((part, i) => {
    if (!part) return;
    const key = `${keyPrefix}${i}`;
    if (/^(\[\d+\])+$/.test(part)) {
      const nums = part.match(/\d+/g)!.map(Number);
      out.push(
        fade(
          key,
          <span className="inline-flex gap-0.5 align-baseline mx-0.5">
            {nums.map((n) => (
              <React.Fragment key={n}>{cite(n)}</React.Fragment>
            ))}
          </span>,
          animate
        )
      );
    } else if (part.startsWith("**") && part.endsWith("**")) {
      out.push(
        <strong key={key} className="font-semibold text-base-content">
          {animate ? words(part.slice(2, -2), `${key}-`) : part.slice(2, -2)}
        </strong>
      );
    } else if (part.startsWith("`") && part.endsWith("`")) {
      out.push(
        fade(
          key,
          <code className="px-1 py-0.5 rounded bg-base-200 text-[0.9em] font-mono">{part.slice(1, -1)}</code>,
          animate
        )
      );
    } else if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      out.push(<em key={key}>{animate ? words(part.slice(1, -1), `${key}-`) : part.slice(1, -1)}</em>);
    } else if (animate) {
      out.push(<React.Fragment key={key}>{words(part, `${key}-`)}</React.Fragment>);
    } else {
      out.push(<React.Fragment key={key}>{part}</React.Fragment>);
    }
  });
  return out;
}

type Block =
  | { type: "h"; level: number; text: string }
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "quote"; text: string };

function parseBlocks(md: string): Block[] {
  const lines = md.replace(/\r/g, "").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      blocks.push({ type: "h", level: h[1].length, text: h[2] });
      i++;
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*•]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    if (/^>\s?/.test(line)) {
      const q: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        q.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "quote", text: q.join(" ") });
      continue;
    }
    const p: string[] = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*[-*•]\s|\s*\d+[.)]\s|>)/.test(lines[i])) {
      p.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: p.join(" ") });
  }
  return blocks;
}

export function renderMarkdown(md: string, cite: CitationRenderer, animate = false): React.ReactNode {
  const blocks = parseBlocks(md);
  return blocks.map((b, i) => {
    switch (b.type) {
      case "h": {
        const cls = b.level <= 2 ? "text-[15px] font-semibold mt-5 mb-1.5" : "text-sm font-semibold mt-4 mb-1";
        return (
          <p key={i} className={`${cls} text-base-content`}>
            {renderInline(b.text, cite, `h${i}-`, animate)}
          </p>
        );
      }
      case "ul":
        return (
          <ul key={i} className="my-2 pl-5 space-y-1 list-disc marker:text-base-content/40">
            {b.items.map((it, j) => (
              <li key={j}>{renderInline(it, cite, `u${i}-${j}-`, animate)}</li>
            ))}
          </ul>
        );
      case "ol":
        return (
          <ol key={i} className="my-2 pl-5 space-y-1 list-decimal marker:text-base-content/50">
            {b.items.map((it, j) => (
              <li key={j}>{renderInline(it, cite, `o${i}-${j}-`, animate)}</li>
            ))}
          </ol>
        );
      case "quote":
        return (
          <blockquote key={i} className="my-2 border-l-2 border-base-300 pl-3 italic text-base-content/80">
            {renderInline(b.text, cite, `q${i}-`, animate)}
          </blockquote>
        );
      default:
        return (
          <p key={i} className="my-2 first:mt-0 last:mb-0">
            {renderInline(b.text, cite, `p${i}-`, animate)}
          </p>
        );
    }
  });
}

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Paces the SSE chunks so text appears at a steady rate instead of in
// bursts. Each frame reveals a slice of the backlog proportional to its
// size: a small backlog trickles out a couple of characters per frame, a
// burst catches up quickly, and the display settles ~100-150 ms behind the
// stream — the same smoothing ChatGPT applies. When the stream ends the
// remainder flushes fast but still animated.
function useSmoothedText(content: string, streaming: boolean, onGrow?: () => void) {
  const [shown, setShown] = useState(() => (streaming && !REDUCED_MOTION ? 0 : content.length));
  const shownRef = useRef(shown);
  useEffect(() => {
    if (REDUCED_MOTION) {
      shownRef.current = content.length;
      setShown(content.length);
      return;
    }
    let raf = 0;
    const tick = () => {
      const backlog = content.length - shownRef.current;
      if (backlog > 0) {
        const step = Math.max(streaming ? 2 : 8, Math.ceil(backlog * (streaming ? 0.12 : 0.35)));
        shownRef.current = Math.min(content.length, shownRef.current + step);
        setShown(shownRef.current);
        onGrow?.();
      }
      if (shownRef.current < content.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [content, streaming, onGrow]);
  return content.slice(0, Math.min(shown, content.length));
}

// Assistant answer body: smoothed reveal + per-word fade while streaming,
// then the plain renderer once the last words have finished fading in.
export function StreamedMarkdown({
  content,
  streaming,
  cite,
  onGrow,
}: {
  content: string;
  streaming: boolean;
  cite: CitationRenderer;
  onGrow?: () => void;
}) {
  const text = useSmoothedText(content, streaming, onGrow);
  const settled = text.length >= content.length && !streaming;
  // Keep animating for a beat after the stream ends so the final words
  // finish their fade instead of snapping to full opacity on re-render.
  const [animate, setAnimate] = useState(streaming);
  useEffect(() => {
    if (!settled) {
      setAnimate(true);
      return;
    }
    const t = window.setTimeout(() => setAnimate(false), 600);
    return () => window.clearTimeout(t);
  }, [settled]);
  return <>{text ? renderMarkdown(text, cite, animate && !REDUCED_MOTION) : null}</>;
}
